import React, { useMemo, useState, useEffect } from 'react';
import { Box, Chip } from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import FlexService from '../../services/FlexService';

/**
 * FilterChipBar - Renders quick-filter chips for workspace grid views.
 *
 * Supports three modes:
 * - Explicit chips: defined in workspace JSON filters.chips[]
 * - Dynamic chips: loaded from a FLEX script via filters.chipsScript
 * - Auto-generated chips: from entity field metadata (allowed_values or status_mapping)
 *
 * Multi-select logic:
 * - Same field: OR (e.g., status = EXPECTED OR PARTIAL)
 * - Cross field: AND (e.g., status = EXPECTED AND overdue = true)
 */
const FilterChipBar = ({ filters, fieldMetadata, activeFilters, onFilterChange, colors }) => {
  // Dynamic chips loaded from chipsScript
  const [dynamicChips, setDynamicChips] = useState([]);
  // values for auto chips, from the field's options script when it has one
  const [autoChipOptions, setAutoChipOptions] = useState(null);

  const autoField = filters?.autoChipsFrom && fieldMetadata ? fieldMetadata.find((f) => f.fieldName === filters.autoChipsFrom) : null;
  const autoScript = autoField?.optionsScript;
  useEffect(() => {
    if (!autoScript) return;
    (async () => {
      try {
        const response = await FlexService.executeScript({ scriptName: autoScript, context: {} });
        const list = response?.context?.result;
        if (response.success && Array.isArray(list)) setAutoChipOptions(list);
      } catch (err) {
        console.error(`Failed to load chip values from script "${autoScript}":`, err);
      }
    })();
  }, [autoScript]);

  useEffect(() => {
    if (!filters?.chipsScript) return;
    (async () => {
      try {
        const response = await FlexService.executeScript({ scriptName: filters.chipsScript, context: {} });
        if (response.success) {
          const ctx = response.context || {};
          const chips = ctx.result || ctx.data || [];
          if (Array.isArray(chips)) setDynamicChips(chips);
        }
      } catch (err) {
        console.error(`Failed to load chips from script "${filters.chipsScript}":`, err);
      }
    })();
  }, [filters?.chipsScript]);

  // Build chip list from explicit config + dynamic + auto-generated
  const chips = useMemo(() => {
    const result = [];

    // Explicit chips from workspace JSON
    if (filters?.chips) {
      result.push(...filters.chips);
    }

    // Dynamic chips from chipsScript
    if (dynamicChips.length > 0) {
      result.push(...dynamicChips);
    }

    // Auto-generated chips from field metadata
    if (filters?.autoChipsFrom && fieldMetadata) {
      const fieldName = filters.autoChipsFrom;
      const field = fieldMetadata.find((f) => f.fieldName === fieldName);
      if (field) {
        const maxValues = filters.autoChipsMaxValues || 10;
        const existingFields = new Set(result.filter((c) => c.field === fieldName).map((c) => String(c.value)));

        let values = [];

        // Try status_mapping first (has color info)
        if (field.statusMapping) {
          const mapping = typeof field.statusMapping === 'string'
            ? JSON.parse(field.statusMapping)
            : field.statusMapping;
          values = Object.entries(mapping).map(([value, config]) => ({
            label: config.label || value,
            field: fieldName,
            value,
            color: mapStatusColor(config.color),
          }));
        }
        // Try the field's options script, then allowed_values
        else if (autoChipOptions || field.allowedValues) {
          const allowed = autoChipOptions || (typeof field.allowedValues === 'string'
            ? JSON.parse(field.allowedValues)
            : field.allowedValues);
          values = allowed.map((v) => ({
            label: typeof v === 'object' ? v.label || v.value : v,
            field: fieldName,
            value: typeof v === 'object' ? v.value : v,
            color: 'default',
          }));
        }

        // Filter out values already covered by explicit chips, apply threshold
        const newValues = values.filter((v) => !existingFields.has(String(v.value)));
        if (newValues.length <= maxValues) {
          result.push(...newValues);
        }
      }
    }

    return result;
  }, [filters, fieldMetadata, dynamicChips, autoChipOptions]);

  if (chips.length === 0) return null;

  // Check if a chip is active
  const isActive = (chip) => {
    return activeFilters.some(
      (f) => f.field === chip.field && String(f.value) === String(chip.value)
    );
  };

  // Toggle a chip filter
  const handleChipClick = (chip) => {
    const active = isActive(chip);
    let newFilters;

    if (active) {
      // Remove this filter
      newFilters = activeFilters.filter(
        (f) => !(f.field === chip.field && String(f.value) === String(chip.value))
      );
    } else {
      // Add this filter
      newFilters = [...activeFilters, { field: chip.field, value: chip.value }];
    }

    onFilterChange(newFilters);
  };

  // Resolve MUI icon by name
  const getIcon = (iconName) => {
    if (!iconName) return undefined;
    const IconComponent = MuiIcons[iconName];
    return IconComponent ? <IconComponent sx={{ fontSize: 14 }} /> : undefined;
  };

  const colorMap = {
    default: { bg: 'rgba(34, 197, 94, 0.12)', text: '#22c55e' },
    info: { bg: 'rgba(59, 130, 246, 0.15)', text: '#3b82f6' },
    warning: { bg: 'rgba(245, 158, 11, 0.15)', text: '#f59e0b' },
    success: { bg: 'rgba(34, 197, 94, 0.15)', text: '#22c55e' },
    error: { bg: 'rgba(239, 68, 68, 0.15)', text: '#ef4444' },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        borderBottom: `1px solid ${colors.border}`,
        bgcolor: colors.surface,
        flexWrap: 'wrap',
      }}
    >
      {chips.map((chip, i) => {
        const active = isActive(chip);
        const chipColor = colorMap[chip.color] || colorMap.default;

        return (
          <Chip
            key={`${chip.field}-${chip.value}-${i}`}
            label={chip.label}
            icon={getIcon(chip.icon)}
            size="small"
            onClick={() => handleChipClick(chip)}
            aria-pressed={active} // on/off is otherwise only a colour
            variant={active ? 'filled' : 'outlined'}
            sx={{
              borderRadius: 0,
              fontSize: '0.75rem',
              height: 26,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              bgcolor: active ? chipColor.bg : 'transparent',
              color: active ? chipColor.text : colors.textMuted,
              borderColor: active ? chipColor.text : colors.border,
              '&:hover': {
                bgcolor: chipColor.bg,
                color: chipColor.text,
                borderColor: chipColor.text,
              },
              '& .MuiChip-icon': {
                color: 'inherit',
              },
            }}
          />
        );
      })}
    </Box>
  );
};

/**
 * Map status_mapping color values to our chip color keys.
 */
const mapStatusColor = (color) => {
  if (!color) return 'default';
  const normalized = color.toLowerCase();
  if (['blue', 'info', 'primary'].includes(normalized)) return 'info';
  if (['yellow', 'orange', 'warning', 'amber'].includes(normalized)) return 'warning';
  if (['green', 'success'].includes(normalized)) return 'success';
  if (['red', 'error', 'danger'].includes(normalized)) return 'error';
  return 'default';
};

export default FilterChipBar;
